import { toast } from '@/components/toast';
import { createAgent, updateAgent } from '@/services/api';
import { logger } from '@/services/logger';
import { useAgentEditorStore } from '@/state/root';
import { AgentStatus, IAgent, IAgentData, LLM, LLM_OPTIONS, Presenter, StreamTextToSpeechVoice, Triggers, VideoType } from '@d-id/types';
import { AxiosError } from 'axios';
import { MutationFunction, useMutation } from 'react-query';
import { getAgentBgObject } from '../utils';

const RETRIES = 3;

const handleError = (
    error: AxiosError<{ kind: string; description?: string; details?: { terms: string[] } }>,
    requestData?: Record<string, any>
) => {
    logger.error('error when create agent', { error, requestData });
    toast.error({ title: 'Failed to create your agent, please try again!', toastId: 'dagent-create-failed' });
};

export interface CreateAgentMutationParams {
    presenter?: Presenter;
    agentName: string;
    voiceProvider?: StreamTextToSpeechVoice;
    starterMessage?: string[];
    greetings?: string[];
    starter_message?: string[];
    prompt_knowledge?: string;
    agentId?: string;
    knowledgeId?: string;
    presenterIsStitch: boolean;
    llm?: LLM;
    triggers: Triggers | undefined;
}

export const useCreateAgent = () => {
    const agentEditorStore = useAgentEditorStore();

    const createAgentMutation: MutationFunction<IAgent | undefined, CreateAgentMutationParams> = async ({
        presenter,
        agentName,
        voiceProvider,
        greetings,
        agentId,
        knowledgeId,
        presenterIsStitch,
        llm,
        triggers,
    }) => {
        const presenterInfoAdapter: Record<VideoType, any> = {
            [VideoType.TALK]: {
                type: 'talk',
                // todo - presenterBackgroundSourceUrl is string, so why we need add alternative values after || ?
                source_url: agentEditorStore.presenterBackgroundSourceUrl || presenter?.poster || presenter?.source_url,
                stitch: presenterIsStitch,
            },
            [VideoType.CLIP]: {
                type: 'clip',
                presenter_id: presenter?.id,
                driver_id: presenter?.driverId,
                ...getAgentBgObject(
                    VideoType.CLIP,
                    agentEditorStore.presenterBackgroundColor,
                    agentEditorStore.presenterBackgroundSourceUrl
                ),
            },
            [VideoType.Scene]: {},
            [VideoType.EXPRESSIVE]: {
                presenter_id: presenter?.id,
            },
            [VideoType.TRAIN]: {},
        };

        const previewThumbnail = agentEditorStore.presenterBackgroundThumbnailUrl || presenter?.thumbnail || presenter?.poster;

        const presenterType = (presenter?.type as VideoType) ?? 'talk';
        const formattedLLM = {
            ...llm,
            provider: llm?.provider ?? ('openai' as const),
            model: llm?.model ?? LLM_OPTIONS.GPT_4O_MINI,
        } as LLM;

        const agent: IAgentData = {
            preview_thumbnail: previewThumbnail,
            preview_name: agentName,
            ...(knowledgeId && {
                knowledge: {
                    id: knowledgeId,
                    provider: 'pinecone',
                },
            }),
            presenter: {
                ...presenter,
                voice: voiceProvider,
                thumbnail: previewThumbnail,
                ...presenterInfoAdapter[presenterType],
            },
            llm: formattedLLM,
            ...(greetings && { greetings }),
            triggers,
        };

        // its for agent create action, from draft to done
        if (agentId) {
            const agentUpdates: { agentId: string; update: Partial<IAgentData> } = {
                agentId,
                update: {
                    ...agent,
                    status: AgentStatus.Started,
                },
            };
            return updateAgent(agentUpdates);
        }

        // its for creation of new agent - "draft"
        return createAgent({
            ...agent,
            version: 2,
            status: AgentStatus.Created,
        });
    };

    return useMutation('create-agent', createAgentMutation, {
        retry(failureCount, error: AxiosError) {
            return (
                failureCount < RETRIES &&
                !!error.response?.status &&
                (error.response.status === 403 || error.response.status === 401 || error.response.status >= 500)
            );
        },
        onError(error: AxiosError<{ kind: string; description?: string; details?: { terms: string[] } }>, requestData) {
            handleError(error, requestData);
        },
    });
};
